import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Grid } from '@mui/material';
import { useLoading } from 'hooks/useLoading';
import IBanner from 'interfaces/IBanner';
import IDestination from 'interfaces/IDestination';
import BannerService from 'services/BannerService';
import DestinationService from 'services/DestinationService';
import ImageService from 'services/ImageService';
import editIcon from 'assets/images/editIcon.svg';
import trashIcon from 'assets/images/trashIcon.svg';
import dragIcon from 'assets/images/dragIcon.svg';
import {
  SettingsList,
  SettingsListItem,
  SettingsListItemButton,
  SettingsListItemButtonList,
  SettingsListItemTitle,
} from 'components/List/styles';
import Heading from 'components/Heading';
import Button from 'components/Button';
import Input from 'components/Input/Input';
import ConfirmationModal from 'components/ConfirmationModal';
import MessageModal from 'common/MessageModal';
import {
  AddImageButton,
  BannerImage,
  CarouselHeader,
  CloseImageButton,
  Paragraph,
  Section,
  SettingsListContainer,
  Video,
  VisualIdentitySection,
} from './styles';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import UploadPortalName from 'components/UploadPortalName';
import UploadLogo from 'components/UploadLogo';
import VisualIdentityService from 'services/VisualIdentityService';
import { IImage } from 'interfaces/IImage';
import { usePortalContext } from 'common/PortalContext';
import { getCdnUrl } from 'utils/env';
import { getLocalUser } from 'services/localStorage';

const Home = (): JSX.Element => {
  const { openLoading, closeLoading } = useLoading();
  const [banners, setBanners] = useState<IBanner[]>([]);
  const [currentBanners, setCurrentBanners] = useState<IBanner[]>([]);
  const [destinations, setDestinations] = useState<IDestination[]>([]);
  const [editingBanners, setEditingBanners] = useState(false);
  const [fileIndex, setFileIndex] = useState(-1);
  const [files, setFiles] = useState<{ [key: string]: File | undefined }>({});
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [targetId, setTargetId] = useState(0);
  const [messageModal, setMessageModal] = useState(false);
  const [messageModalText, setMessageModalText] = useState('');
  const inputFile = useRef<HTMLInputElement>(null);
  const [newLogo, setNewLogo] = useState<File | string | IImage[] | null>(null);
  const [newTouristMap, setNewTouristMap] = useState<File | string | IImage[] | null>(null);
  const [portalName, setPortalName] = useState<string>('');
  const portalContext = usePortalContext();

  const localUser = getLocalUser();
  const isSuperAdmin = localUser?.profile?.name === 'admin';

  const service = {
    banner: new BannerService(),
    destination: new DestinationService(),
    file: new ImageService(),
  };

  useEffect(() => {
    openLoading();
    const fetchData = [fetchBanners(), fetchDestinations(), fetchPortalVisualIdentity()];
    Promise.all(fetchData).finally(() => closeLoading());
  }, []);

  const fetchPortalVisualIdentity = async () => {
    const visualIdentityService = new VisualIdentityService();
    const portalNameData = await visualIdentityService.getPortalName();
    setPortalName(portalNameData as unknown as string);

    const logoData = await visualIdentityService.getLogo();
    if (logoData) {
      const logoUrl = getCdnUrl(logoData as unknown as string);
      setNewLogo([{ id: 0, name: 'porto-seguro-logo', url: logoUrl }]);
      portalContext.setPortalLogo([{ id: 0, name: 'porto-seguro-logo', url: logoUrl }]);
    }

    await fetchTouristMap();
  };

  const fetchTouristMap = async () => {
    try {
      const touristMapData = await new VisualIdentityService().getTouristMap();
      if (touristMapData) {
        const formattedUrl = touristMapData.startsWith('data:') ? touristMapData : getCdnUrl(touristMapData);
        setNewTouristMap([{ id: 0, name: 'mapa-turistico', url: formattedUrl }]);
      }
    } catch (e) {
      console.error('Erro ao buscar mapa turístico', e);
    }
  };

  const fetchBanners = async () => {
    const data = await service.banner.findAll();
    data.sort((a, b) => b.id - a.id);

    setBanners(data);
    setCurrentBanners(JSON.parse(JSON.stringify(data)));
  };

  const saveBannersEditing = async () => {
    const emptyImage = banners.some(({ image }) => !image);

    if (emptyImage) {
      showModal('Selecione uma imagem para todos os itens do carrossel!');
      return;
    }

    openLoading();
    for (const banner of banners.reverse()) {
      let image = banner.image;
      const file = files[image];
      if (file) {
        image = await service.file.upload(file);
      }

      await service.banner.create({ ...banner, image });
    }

    const deleteBanners = currentBanners.map(banner => {
      return service.banner.remove(banner.id);
    });

    await Promise.all(deleteBanners);

    await fetchBanners();
    setEditingBanners(false);
    closeLoading();
  };

  const cancelBannersEditing = () => {
    setBanners(JSON.parse(JSON.stringify(currentBanners)));
    setEditingBanners(false);
  };

  const addBanner = () => {
    setEditingBanners(true);
    const newBanner = { id: 0, description: '', image: '', link: '', title: '' };
    setBanners(current => [...current, newBanner]);
  };

  const deleteBanner = () => {
    setEditingBanners(true);
    setBanners(
      banners.filter(banner => {
        return banner.id !== targetId;
      })
    );
  };

  const fetchDestinations = async () => {
    const data = await service.destination.findAll();
    setDestinations(data);
  };

  const showModal = (message: string) => {
    setMessageModalText(message);
    setMessageModal(true);
  };

  const handleInputFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event?.target?.files?.[0]) {
      return;
    }

    const file = event.target.files[0];
    const objectUrl = `${URL.createObjectURL(file)}#${file.name}`;

    setFiles({ ...files, [objectUrl]: file });
    updateBannerImage(objectUrl);
  };

  const updateBannerImage = (image: string, current = fileIndex) => {
    const updateBanners = banners.map((banner, index) => {
      if (current === index) {
        return { ...banner, image };
      }
      return { ...banner };
    });

    setBanners(updateBanners);
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(banners);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setBanners(items);
  };

  const handleInputTitleChange = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    setEditingBanners(true);
    const updatedBanners = banners.map(banner => {
      if (banner.id === id) {
        return { ...banner, title: event.target.value };
      }
      return banner;
    });

    setBanners(updatedBanners);
  };

  const handleInputURLChange = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    setEditingBanners(true);
    const updatedBanners = banners.map(banner => {
      if (banner.id === id) {
        return { ...banner, link: event.target.value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const fetchNewLogo = async () => {
    const logoData = await new VisualIdentityService().getLogo();
    if (logoData) {
      const logoUrl = getCdnUrl(logoData as unknown as string);
      portalContext.setPortalLogo([{ id: 0, name: 'porto-seguro-logo', url: logoUrl }]);
    }
  };

  const uploadNewLogo = async () => {
    openLoading();
    try {
      if (newLogo instanceof File) {
        await new VisualIdentityService().patchLogo(newLogo);
        fetchNewLogo();
      } else {
        console.error('Arquivo inválido');
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const newLogoAsString = reader.result as string;
          await new VisualIdentityService().postLogo(newLogoAsString, portalName);
          fetchNewLogo();
        };
        reader.readAsDataURL(newLogo as Blob);
      } else {
        console.error('Erro ao atualizar imagem do cabeçalho', error);
      }
    } finally {
      closeLoading();
    }
  };

  const uploadTouristMap = async () => {
    openLoading();
    try {
      if (newTouristMap instanceof File) {
        await new VisualIdentityService().patchTouristMap(newTouristMap);
        await fetchTouristMap();
      } else {
        console.error('Arquivo de mapa turístico inválido');
      }
    } catch (error) {
      console.error('Erro ao atualizar imagem do mapa turístico', error);
    } finally {
      closeLoading();
    }
  };

  return (
    <Box>
      <VisualIdentitySection>
        <Section>
          <UploadLogo
            title="Imagem do Cabeçalho"
            descriptionText="O arquivo deve ser .SVG e ter dimensões mínimas de 360 x 280 pixels para garantir a qualidade da visualização."
            multipleFiles={false}
            accept="image/svg+xml"
            limitFiles={1}
            importedFiles={Array.isArray(newLogo) ? newLogo : undefined}
            htmlFor="image"
            onChange={files => {
              const selectedFile = files.find(f => f instanceof File) || files[files.length - 1];
              setNewLogo(selectedFile as File);
            }}
            onSubmit={uploadNewLogo}
          />
        </Section>
        {isSuperAdmin && (
          <Section>
            <UploadLogo
              title="Imagem do Mapa Turístico"
              descriptionText="O arquivo deve ser .SVG para garantir a qualidade da visualização do mapa."
              multipleFiles={false}
              accept="image/svg+xml"
              limitFiles={1}
              importedFiles={Array.isArray(newTouristMap) ? newTouristMap : undefined}
              htmlFor="tourist-map-image"
              onChange={files => {
                const selectedFile = files.find(f => f instanceof File) || files[files.length - 1];
                setNewTouristMap(selectedFile as File);
              }}
              onSubmit={uploadTouristMap}
              onCancel={fetchTouristMap}
            />
          </Section>
        )}

        <Section>
          <UploadPortalName name={portalName} />
        </Section>
      </VisualIdentitySection>

      <Section>
        <CarouselHeader>
          <Heading>Carrossel</Heading>
          <Grid container direction="row" justifyContent="flex-end" alignItems="center" columnSpacing={4}>
            <Grid item>
              {!editingBanners && (
                <Button buttonType="secondary" onClick={() => setEditingBanners(true)}>
                  Editar
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button onClick={addBanner}>Adicionar</Button>
            </Grid>
          </Grid>
        </CarouselHeader>
        <Paragraph>
          Para garantir a qualidade da visualização, as imagens do carrossel precisam ter dimensão mínima de 1366 x 600
          pixels (a ordem das imagens no carrossel será a mesma mostrada abaixo)
        </Paragraph>
        <SettingsListContainer>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="carousel-list" type="list" direction="vertical" isDropDisabled={!editingBanners}>
              {provided => (
                <SettingsList className="carousel-list" ref={provided.innerRef} {...provided.droppableProps}>
                  {banners.map((banner, index) => (
                    <Draggable
                      key={index}
                      draggableId={index.toString()}
                      index={index}
                      isDragDisabled={!editingBanners}>
                      {provided => (
                        <SettingsListItem
                          key={index}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}>
                          {editingBanners && (
                            <SettingsListItemTitle>
                              <div>
                                <img draggable={false} src={dragIcon} alt="icon for drag item" />
                              </div>
                            </SettingsListItemTitle>
                          )}
                          <SettingsListItemTitle>
                            <BannerImage
                              style={{
                                ...(banner.image && {
                                  background: `url(${banner.image}) center center / cover no-repeat`,
                                }),
                                ...(!banner.image && { backgroundColor: '#d9d9d9' }),
                              }}>
                              {banner.image && banner.image.endsWith('.mp4') && (
                                <Video src={banner?.image} autoPlay loop muted playsInline />
                              )}
                              {editingBanners &&
                                (!banner.image ? (
                                  <div className="add-image-button-container">
                                    <AddImageButton
                                      onClick={() => {
                                        inputFile?.current?.click();
                                        setFileIndex(index);
                                      }}>
                                      +
                                    </AddImageButton>
                                  </div>
                                ) : (
                                  <CloseImageButton onClick={() => updateBannerImage('', index)}>X</CloseImageButton>
                                ))}
                            </BannerImage>
                          </SettingsListItemTitle>
                          <SettingsListItemTitle className="input-box">
                            {editingBanners ? (
                              <Input
                                type="text"
                                defaultValue={banner.title}
                                onChange={e => handleInputTitleChange(e, banner.id)}
                                className="input-title"
                                placeholder="Digite o título do item"
                                value={banner.title}
                              />
                            ) : (
                              banner.title
                            )}
                          </SettingsListItemTitle>
                          <SettingsListItemTitle className="input-box">
                            {editingBanners ? (
                              <Input
                                type="text"
                                defaultValue={banner.link}
                                onChange={e => handleInputURLChange(e, banner.id)}
                                className="input-link"
                                placeholder="Digite o link de redirecionamento"
                                value={banner.link}
                              />
                            ) : (
                              banner.link
                            )}
                          </SettingsListItemTitle>
                          <SettingsListItemButtonList>
                            <SettingsListItemButton
                              onClick={() => {
                                setConfirmDeletion(true);
                                setTargetId(banner.id);
                              }}>
                              <img src={trashIcon} alt="icon for trash item" />
                            </SettingsListItemButton>
                          </SettingsListItemButtonList>
                        </SettingsListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </SettingsList>
              )}
            </Droppable>
          </DragDropContext>
        </SettingsListContainer>
        {editingBanners && (
          <Grid container direction="row" justifyContent="flex-end" alignItems="center" columnSpacing={4}>
            <Grid item>
              <Button buttonType="secondary" onClick={cancelBannersEditing}>
                Cancelar
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={saveBannersEditing}>Salvar</Button>
            </Grid>
          </Grid>
        )}
      </Section>
      <Section>
        <Heading>Lista de Destinos</Heading>
        <SettingsListContainer>
          <SettingsList>
            {destinations.map((destination, index) => (
              <SettingsListItem key={index}>
                <SettingsListItemTitle>{destination.title}</SettingsListItemTitle>
                <SettingsListItemButtonList>
                  <Link to={`/admin/destination/${destination.id}`}>
                    <SettingsListItemButton>
                      <img src={editIcon} alt="icon for edit item" />
                    </SettingsListItemButton>
                  </Link>
                </SettingsListItemButtonList>
              </SettingsListItem>
            ))}
          </SettingsList>
        </SettingsListContainer>
      </Section>
      <input type="file" ref={inputFile} style={{ display: 'none' }} onChange={handleInputFileChange} />
      <ConfirmationModal
        open={confirmDeletion}
        title="EXCLUSÃO DE ITEM"
        message="Você tem certeza de que deseja excluir esse item?"
        onCancel={() => setConfirmDeletion(false)}
        onConfirm={() => {
          setConfirmDeletion(false);
          deleteBanner();
        }}
      />
      <MessageModal
        title="Aviso"
        isOpen={messageModal}
        message={messageModalText}
        onClose={() => setMessageModal(false)}
      />
    </Box>
  );
};

export default Home;
